import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "toggle",       // Clickable toggle icons for each step
    "statusText",   // Element to show "Completed by X at Y" text
    "progressBar",  // The progress bar fill element
    "progressText", // The percentage text (e.g., "50%")
    "checkpointStatus" // Element that shows "X of Y complete"
  ]
  
  static values = {
    objectiveId: String,
    userObjectiveId: { type: String, default: "" },
    completeUrl: String,
    uncompleteUrl: String
  }

  // Track if a request is in progress to prevent double-submissions
  isSubmitting = false
  
  connect() {
    // Initialize any state needed on connection
    this.updateProgressBar()
    this.updateSequentialStates()
    this.updateCheckpointStatus()
    this.updateConnectors()
  }
  
  // Handle toggle click
  toggle(event) {
    // Prevent default link behaviour & stop bubbling to avoid interference
    event.preventDefault()
    event.stopPropagation()

    // Prevent rapid toggling by debouncing
    if (this.isSubmitting) {
      event.preventDefault()
      return
    }
    
    // Click may originate from the inner <i> icon; ensure we act on the
    // element that has the `data-step-progress-target="toggle"` attribute.
    const toggleEl =
      event.currentTarget.closest('[data-step-progress-target="toggle"]') ||
      event.currentTarget
    const isCompleted = toggleEl.dataset.completed === "true"
    
    // Start loading state
    this.isSubmitting = true
    toggleEl.classList.add('pointer-events-none', 'opacity-50')
    
    if (!isCompleted) {
      this.markComplete(toggleEl)
    } else {
      this.markIncomplete(toggleEl)
    }
  }
  
  // Mark a step as complete via POST request
  markComplete(toggleEl) {
    const statusElement = this.getStatusElementForToggle(toggleEl)
    const token = document.querySelector('meta[name="csrf-token"]').getAttribute('content')
    const stepId = toggleEl.dataset.missionStepId

    // ----- optimistic UI -----
    this.applyCompleted(toggleEl)
    statusElement.classList.add('hidden')
    statusElement.textContent = ''
    this.incrementProgress()
    this.updateSequentialStates()
    this.updateCheckpointStatus()
    this.updateConnectors()
    
    // Prepare the URL with proper parameters
    const url = this.completeUrlValue ||
                `/objectives/${this.objectiveIdValue}/steps/${stepId}/progress`
    
    // Create form data for the request
    const formData = new FormData()
    if (this.userObjectiveIdValue) {
      formData.append('user_objective_id', this.userObjectiveIdValue)
    } else {
      formData.append('objective_id', this.objectiveIdValue)
    }
    
    fetch(url, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': token,
        'Accept': 'application/json'
      },
      body: formData
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok')
      }
      return response.json()
    })
    .then(data => {
      // Finalize status text with server data
      statusElement.textContent = `Completed by ${data.completed_by_name} on ${data.completed_at}`
      statusElement.classList.remove('hidden')

      // Server signals that this is the user's first checkpoint and they
      // haven't named a startup yet — open the modal to collect it.
      if (data.prompt_startup_name) {
        document.dispatchEvent(new CustomEvent('startup-name-prompt:open'))
      }
    })
    .catch(error => {
      console.error('Error:', error)
      // Revert optimistic update
      this.applyIncomplete(toggleEl)
      statusElement.textContent = ''
      statusElement.classList.add('hidden')
      this.decrementProgress()
      this.updateSequentialStates()
      this.updateCheckpointStatus()
      this.updateConnectors()
      
      // Show error notification if desired
      // this.showError("Could not mark step as complete")
    })
    .finally(() => {
      // Reset loading state
      this.isSubmitting = false
      toggleEl.classList.remove('pointer-events-none', 'opacity-50')
    })
  }
  
  // Mark a step as incomplete via DELETE request
  markIncomplete(toggleEl) {
    const statusElement = this.getStatusElementForToggle(toggleEl)
    const token = document.querySelector('meta[name="csrf-token"]').getAttribute('content')
    const stepId = toggleEl.dataset.missionStepId

    // ----- optimistic UI -----
    this.applyIncomplete(toggleEl)
    statusElement.textContent = ''
    statusElement.classList.add('hidden')
    this.decrementProgress()
    this.updateSequentialStates()
    this.updateCheckpointStatus()
    this.updateConnectors()
    
    // Prepare the URL with proper parameters
    const url = this.uncompleteUrlValue ||
                `/objectives/${this.objectiveIdValue}/steps/${stepId}/progress`

    // Send the user_objective_id (or fall back to objective_id) so the server
    // resolves the same UserObjective for DELETE that it used for POST.
    // Without this, the server falls into a status-filtered find_or_create
    // branch and 404s when the user's UserObjective is :paused.
    const formData = new FormData()
    if (this.userObjectiveIdValue) {
      formData.append('user_objective_id', this.userObjectiveIdValue)
    } else {
      formData.append('objective_id', this.objectiveIdValue)
    }

    fetch(url, {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': token,
        'Accept': 'application/json'
      },
      body: formData
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok')
      }
      return response.json()
    })
    .then(() => {
      // nothing further – optimistic UI already correct
    })
    .catch(error => {
      console.error('Error:', error)
      // Revert optimistic change
      this.applyCompleted(toggleEl)
      // Note: we keep status hidden, could re-fetch if needed
      this.incrementProgress()
      this.updateSequentialStates()
      this.updateCheckpointStatus()
      this.updateConnectors()
      
      // Show error notification if desired
      // this.showError("Could not mark step as incomplete")
    })
    .finally(() => {
      // Reset loading state
      this.isSubmitting = false
      toggleEl.classList.remove('pointer-events-none', 'opacity-50')
    })
  }
  
  // Helper to find the status text element associated with a toggle
  getStatusElementForToggle(toggleEl) {
    // Find the closest parent with data-step-id attribute
    const stepContainer = toggleEl.closest('[data-step-id]')
    return stepContainer.querySelector('[data-step-progress-target="statusText"]')
  }
  
  // Update the progress bar when a step is completed
  incrementProgress() {
    const totalSteps = this.toggleTargets.length
    const completedSteps = this.toggleTargets.filter(el => el.dataset.completed === "true").length
    this.updateProgressBar(completedSteps, totalSteps)
  }
  
  // Update the progress bar when a step is uncompleted
  decrementProgress() {
    const totalSteps = this.toggleTargets.length
    const completedSteps = this.toggleTargets.filter(el => el.dataset.completed === "true").length
    this.updateProgressBar(completedSteps, totalSteps)
  }
  
  // Update the progress bar and percentage text
  updateProgressBar(completedSteps, totalSteps) {
    // If values aren't provided, calculate from current state
    if (completedSteps === undefined || totalSteps === undefined) {
      totalSteps = this.toggleTargets.length
      completedSteps = this.toggleTargets.filter(el => el.dataset.completed === "true").length
    }
    
    const percent = totalSteps > 0 ? Math.floor((completedSteps / totalSteps) * 100) : 0
    
    // Update progress bar width
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${percent}%`
    }
    
    // Update percentage text
    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = `${percent}%`
    }
  }

  // Update "X of Y complete" text if target present
  updateCheckpointStatus() {
    if (!this.hasCheckpointStatusTarget) return
    const total = this.toggleTargets.length
    const completed = this.toggleTargets.filter(el => el.dataset.completed === "true").length
    this.checkpointStatusTarget.textContent = `${completed} of ${total} complete`
  }

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  // Apply visual state: completed (green check)
  applyCompleted(toggleEl) {
    toggleEl.dataset.completed = 'true'
    toggleEl.dataset.state = 'completed'
    toggleEl.classList.remove(
      'bg-warning',
      'ring-4',
      'ring-warning/20',
      'bg-section-bg'
    )
    toggleEl.classList.add('bg-green-500')
    toggleEl.innerHTML =
      '<i class="fas fa-check text-white text-sm" data-action="click->step-progress#toggle"></i>'
  }

  // Apply visual state: current (orange with halo, numbered)
  applyCurrent(toggleEl) {
    const n = toggleEl.dataset.stepNumber || ''
    toggleEl.dataset.completed = 'false'
    toggleEl.dataset.state = 'current'
    toggleEl.classList.remove(
      'bg-green-500',
      'bg-section-bg',
      'ring-4',
      'ring-warning/20',
      'ring-orange-200'
    )
    toggleEl.classList.add('bg-warning', 'ring-4', 'ring-orange-200')
    toggleEl.innerHTML = `<span class="text-white font-bold text-sm" data-action="click->step-progress#toggle">${n}</span>`
  }

  // Apply visual state: incomplete (gray, numbered)
  applyIncomplete(toggleEl) {
    const n = toggleEl.dataset.stepNumber || ''
    toggleEl.dataset.completed = 'false'
    toggleEl.dataset.state = 'incomplete'
    toggleEl.classList.remove(
      'bg-green-500',
      'bg-warning',
      'ring-4',
      'ring-warning/20',
      'ring-orange-200'
    )
    toggleEl.classList.add('bg-section-bg')
    toggleEl.innerHTML = `<span class="text-text-muted font-bold text-sm" data-action="click->step-progress#toggle">${n}</span>`
  }

  // Recompute sequential states so the first incomplete is "current"
  updateSequentialStates() {
    let setCurrent = false
    this.toggleTargets.forEach((el) => {
      if (el.dataset.completed === 'true') {
        if (el.dataset.state !== 'completed') this.applyCompleted(el)
        return
      }

      if (!setCurrent) {
        this.applyCurrent(el)
        setCurrent = true
      } else {
        this.applyIncomplete(el)
      }
    })
    // After states shift, fix connector visuals
    this.updateConnectors()
  }

  // -------------------------------------------------------------------------
  // Connector sync
  // -------------------------------------------------------------------------

  // Ensure each connector line reflects completion state
  updateConnectors() {
    this.toggleTargets.forEach((toggleEl) => {
      const stepContainer = toggleEl.closest('[data-step-id]')
      if (!stepContainer) return
      const connector = stepContainer.querySelector('.step-connector')
      if (!connector) return

      if (toggleEl.dataset.completed === 'true') {
        connector.classList.add('completed')
      } else {
        connector.classList.remove('completed')
      }
    })
  }
}
